/**
 * /journals/:journalId — journal detail page.
 *
 * Fetches GET /api/journals/{journalId} (with lines eager-loaded).
 * Displays:
 *   - Header: journal_no, date, source, status chip, posted_at, description,
 *             reverses_journal_id note
 *   - Lines table: account (resolved from accounts list), memo, debit, credit
 *   - Totals row: must be balanced
 *   - Reverse panel: shown only when the API allows generic GL reversal
 *     → POST /api/journals/{id}/reverse
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Chip, Stack, TextField,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  accountsListQueryOptions,
  glKeys,
  journalDetailQueryOptions,
  journalKeys,
  organizationCurrentQueryOptions,
  periodsListQueryOptions,
  trialBalanceKeys,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { codeAndName } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/journals/$journalId")({
  component: JournalDetailPage,
});

const STATUS_COLOR: Record<string, "success" | "default" | "warning"> = {
  posted: "success",
  draft: "default",
  reversed: "warning",
};

function fmtMoney(n: number | string | null, currency = "USD"): string {
  if (n == null || n === "" || n === "0") return "—";
  const v = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(v) || v === 0) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(v);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function JournalDetailPage() {
  const { journalId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const journalQuery = useQuery(journalDetailQueryOptions(journalId));
  const accountsQuery = useQuery(accountsListQueryOptions());
  const periodsQuery = useQuery(periodsListQueryOptions());
  const organizationQuery = useQuery(organizationCurrentQueryOptions());
  const baseCurrency = organizationQuery.data?.base_currency_code ?? "USD";

  // Build account label lookup
  const accountLabel = useMemo(() => {
    const m: Record<string, string> = {};
    (accountsQuery.data ?? []).forEach((a) => { m[a.id] = codeAndName(a); });
    return (id: string) => m[id] ?? id.slice(0, 8) + "…";
  }, [accountsQuery.data]);

  // Reverse form state
  const [reversalDate, setReversalDate] = useState(todayIso());
  const [reversalPeriodId, setReversalPeriodId] = useState("");
  const [reversalDescription, setReversalDescription] = useState("");

  // Auto-derive period from reversal date
  useEffect(() => {
    if (!periodsQuery.data || !reversalDate) return;
    const match = periodsQuery.data.find(
      (p) => reversalDate >= p.start_date && reversalDate <= p.end_date,
    );
    if (match) setReversalPeriodId(match.id);
  }, [reversalDate, periodsQuery.data]);

  const reverse = useMutation({
    mutationFn: () =>
      api(`/api/journals/${journalId}/reverse`, {
        method: "POST",
        body: {
          reversal_date: reversalDate,
          period_id: reversalPeriodId,
          description: reversalDescription || `Reversal of journal #${j?.journal_no}`,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glKeys.all });
      qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      qc.invalidateQueries({ queryKey: journalKeys.all });
      toast.success("Journal reversed");
      navigate({ to: "/journals" });
    },
    onError: (err: Error) => toast.error(`Reverse failed: ${err.message}`),
  });

  const requestReverse = () => {
    if (!j) return;
    confirmAction({
      title: "Reverse this journal?",
      message:
        `Journal #${j.journal_no} (${j.journal_date}) will be marked 'reversed'. ` +
        `A new journal will be posted on ${reversalDate} with debits and credits swapped. ` +
        `The original lines stay in the GL — both entries are permanent.`,
      confirmLabel: "Reverse journal",
      kind: "danger",
      onConfirm: async () => { await reverse.mutateAsync(); },
    });
  };

  const j = journalQuery.data;

  const canReverse = j?.can_reverse === true;

  const totalDebit = useMemo(
    () => (j?.lines ?? []).reduce((s, l) => s + Number(l.base_debit ?? 0), 0),
    [j],
  );
  const totalCredit = useMemo(
    () => (j?.lines ?? []).reduce((s, l) => s + Number(l.base_credit ?? 0), 0),
    [j],
  );
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005;

  if (journalQuery.isLoading) {
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }

  if (!j) {
    return (
      <div className="p-6">
        <div className="text-sm text-red-600">Journal not found.</div>
        <button
          className="mt-4 text-sm text-blue-600 hover:underline"
          onClick={() => navigate({ to: "/journals" })}
        >
          ← Back to journals
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-56px)] overflow-auto">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="text-xs text-slate-500 mb-1 dark:text-slate-400">
          <Link to="/journals" className="hover:text-slate-700 dark:hover:text-slate-200">
            Journals
          </Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">
            Journal #{j.journal_no}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap mt-1">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 font-mono">
            Journal #{j.journal_no}
          </h1>
          <Chip
            label={j.status}
            size="small"
            color={STATUS_COLOR[j.status] ?? "default"}
            variant={j.status === "posted" ? "filled" : "outlined"}
          />
          {j.reverses_journal_id && (
            <Chip size="small" label="reversal entry" color="warning" variant="outlined" />
          )}
          <span className="text-sm text-slate-600 dark:text-slate-300">
            {j.journal_date}
          </span>
          <span className="text-xs text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 dark:bg-gray-800 dark:text-slate-400">
            {j.source.replace(/_/g, " ")}
          </span>
        </div>
        {j.description && (
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{j.description}</p>
        )}
        {j.posted_at && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            Posted at: {new Date(j.posted_at).toLocaleString()}
          </p>
        )}
        {j.reverses_journal_id && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Reverses journal:{" "}
            <Link
              to="/journals/$journalId"
              params={{ journalId: j.reverses_journal_id }}
              className="text-blue-600 hover:underline font-mono"
            >
              {j.reverses_journal_id.slice(0, 8)}…
            </Link>
          </p>
        )}
      </div>

      {/* Lines */}
      <div className="px-6 py-4 flex-1">
        <section className="bg-white rounded-lg border border-slate-200 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-300">
            Journal Lines
            <span className="ml-2 text-xs font-normal text-slate-400">
              {j.lines.length} line{j.lines.length === 1 ? "" : "s"}
            </span>
          </h2>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-gray-800 dark:text-slate-400">
                <th className="py-2 w-8">#</th>
                <th className="py-2 min-w-[220px]">Account</th>
                <th className="py-2">Memo</th>
                <th className="py-2 w-32 text-right">Debit</th>
                <th className="py-2 w-32 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {j.lines.map((ln) => (
                <tr
                  key={ln.id}
                  className="border-b border-slate-100 dark:border-gray-800"
                >
                  <td className="py-2 text-slate-400 font-mono text-xs dark:text-slate-500">
                    {ln.line_no}
                  </td>
                  <td className="py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                    {accountLabel(ln.account_id)}
                  </td>
                  <td className="py-2 text-slate-600 dark:text-slate-400 text-xs">
                    {ln.memo ?? ""}
                  </td>
                  <td className="py-2 text-right font-mono text-sm">
                    {fmtMoney(ln.debit, ln.currency_code)}
                  </td>
                  <td className="py-2 text-right font-mono text-sm">
                    {fmtMoney(ln.credit, ln.currency_code)}
                  </td>
                </tr>
              ))}
              {/* Totals */}
              <tr className={`border-t-2 ${
                balanced
                  ? "bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-800"
                  : "bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700"
              }`}>
                <td colSpan={3} className="py-2 text-right text-xs uppercase tracking-wide text-slate-500 pr-4 dark:text-slate-400">
                  Base totals ({baseCurrency}) {balanced ? "✓ balanced" : "⚠ out of balance"}
                </td>
                <td className="py-2 text-right font-mono font-semibold">
                  {fmtMoney(totalDebit, baseCurrency)}
                </td>
                <td className="py-2 text-right font-mono font-semibold">
                  {fmtMoney(totalCredit, baseCurrency)}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Reverse panel */}
        {canReverse && (
          <section className="mt-4 bg-amber-50 rounded-lg border border-amber-200 p-4 dark:bg-amber-900/10 dark:border-amber-800">
            <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">
              Reverse this journal
            </h2>
            <p className="text-xs text-amber-800 dark:text-amber-400 mb-3">
              Posts a new mirrored journal on the date you choose. Both entries
              stay in the GL permanently — that is the audit trail.
            </p>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Reversal date"
                type="date"
                size="small"
                value={reversalDate}
                onChange={(e) => setReversalDate(e.target.value)}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Period"
                select
                size="small"
                value={reversalPeriodId}
                onChange={(e) => setReversalPeriodId(e.target.value)}
                sx={{ flex: 1 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">— pick a period —</option>
                {(periodsQuery.data ?? []).map((p) => (
                  <option key={p.id} value={p.id} disabled={p.status !== "open"}>
                    Period {p.period_no} · {p.start_date} → {p.end_date} · {p.status}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Description"
                size="small"
                value={reversalDescription}
                onChange={(e) => setReversalDescription(e.target.value)}
                placeholder={`Reversal of journal #${j.journal_no}`}
                sx={{ flex: 2 }}
              />
            </Stack>
            <div className="mt-3 flex justify-end">
              <Button
                variant="contained"
                color="warning"
                disabled={!reversalPeriodId || reverse.isPending}
                onClick={requestReverse}
              >
                {reverse.isPending ? "Reversing…" : "Reverse journal"}
              </Button>
            </div>
          </section>
        )}

        {!canReverse && j.reversal_block_reason && (
          <div className="mt-4 text-xs text-slate-500 italic dark:text-slate-400">
            {j.reversal_block_reason}
          </div>
        )}
      </div>
    </div>
  );
}
